import React, { useMemo } from "react";
import { formatDate } from "@/lib/api";
import { StatusBadge } from "@/components/app/ui-bits";
import { Flag } from "@phosphor-icons/react";

/**
 * ProjectDeliverablesGantt — lightweight, dependency-free Gantt strip.
 *
 * Each deliverable becomes a horizontal bar positioned between its
 * start_date and due_date, colour-coded by status, with the
 * completion_pct rendered as a filled "progress" inside the bar.
 * Sub-breakdowns are visually indented under their parent (via the same
 * parent_deliverable_id grouping used by the tree).
 *
 * Designed to fit inside the Deliverables Sheet — no x-axis labels for
 * sub-day precision; the timeline is laid out left→right over the
 * combined [min(start), max(due)] window with month gridlines.
 */
const STATUS_COLOR = {
  pending:     "bg-slate-300",
  in_progress: "bg-blue-500",
  review:      "bg-amber-500",
  approved:    "bg-emerald-500",
  completed:   "bg-emerald-600",
  blocked:     "bg-rose-500",
  rejected:    "bg-rose-400",
  returned:    "bg-orange-400",
  draft:       "bg-slate-300",
};

function parseDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default function ProjectDeliverablesGantt({ project }) {
  const deliverables = project?.deliverables || [];

  // Compute timeline window and lay out children directly under their
  // parent so sub-breakdowns appear nested.
  const { rows, windowStart, windowEnd, months } = useMemo(() => {
    if (deliverables.length === 0) {
      return { rows: [], windowStart: null, windowEnd: null, months: [] };
    }
    // Order: roots in sort_order, then children right after each parent
    const byParent = new Map();
    deliverables.forEach((d) => {
      const k = d.parent_deliverable_id || "__root__";
      if (!byParent.has(k)) byParent.set(k, []);
      byParent.get(k).push(d);
    });
    for (const arr of byParent.values()) {
      arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }
    const flat = [];
    const walk = (parentId, depth) => {
      const items = byParent.get(parentId) || [];
      for (const d of items) {
        flat.push({ ...d, __depth: depth });
        walk(d.id, depth + 1);
      }
    };
    walk("__root__", 0);

    // Compute window from earliest start_date or due_date to latest due_date
    let mn = null, mx = null;
    flat.forEach((d) => {
      const s = parseDate(d.start_date) || parseDate(d.due_date);
      const e = parseDate(d.due_date) || parseDate(d.start_date);
      if (s && (!mn || s < mn)) mn = s;
      if (e && (!mx || e > mx)) mx = e;
    });
    if (!mn || !mx) {
      // Fallback to current month if no dates
      mn = new Date();
      mx = new Date(mn.getTime() + 90 * 86_400_000);
    }
    // Pad window by 5% each side for visual comfort
    const span = mx - mn;
    mn = new Date(mn.getTime() - span * 0.05);
    mx = new Date(mx.getTime() + span * 0.05);

    // Compute month gridlines
    const monthList = [];
    const cur = new Date(mn.getFullYear(), mn.getMonth(), 1);
    while (cur <= mx) {
      monthList.push(new Date(cur));
      cur.setMonth(cur.getMonth() + 1);
    }
    return { rows: flat, windowStart: mn, windowEnd: mx, months: monthList };
  }, [deliverables]);

  if (deliverables.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-xs text-slate-500"
           data-testid={`gantt-empty-${project.id}`}>
        No deliverables to chart yet. Add deliverables first.
      </div>
    );
  }

  const totalMs = windowEnd - windowStart;
  const pct = (d) => Math.max(0, Math.min(100, ((d - windowStart) / totalMs) * 100));

  return (
    <div className="rounded-md border border-slate-200 bg-white" data-testid={`gantt-${project.id}`}>
      {/* Header row — month labels */}
      <div className="flex border-b border-slate-200 bg-slate-50 text-[10px] font-mono-alt uppercase tracking-wider text-slate-600">
        <div className="w-[230px] flex-shrink-0 px-3 py-2 border-r border-slate-200">Deliverable</div>
        <div className="flex-1 relative h-7">
          {months.map((m, i) => {
            const left = pct(m);
            const next = months[i + 1] || windowEnd;
            const width = pct(next) - left;
            return (
              <div key={i} className="absolute top-0 h-full flex items-center border-l border-slate-200 px-1"
                   style={{ left: `${left}%`, width: `${width}%` }}>
                {m.toLocaleDateString(undefined, { month: "short", year: "2-digit" })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Body rows */}
      <ul className="divide-y divide-slate-100">
        {rows.map((d) => {
          const start = parseDate(d.start_date) || parseDate(d.due_date);
          const end   = parseDate(d.due_date)   || parseDate(d.start_date);
          if (!start || !end) {
            return (
              <li key={d.id} className="flex items-center text-xs">
                <div className="w-[230px] flex-shrink-0 px-3 py-2 text-slate-500 border-r border-slate-100"
                     style={{ paddingLeft: 12 + d.__depth * 16 }}>
                  {d.milestone && <Flag size={9} weight="fill" className="inline mr-1 text-amber-600" />}
                  <span className={d.__depth > 0 ? "text-slate-700" : "font-medium text-slate-900"}>{d.title || "Untitled"}</span>
                  <span className="ml-1 font-mono-alt text-[10px] text-slate-400">no dates</span>
                </div>
                <div className="flex-1 py-2 px-3">
                  <span className="font-mono-alt text-[10px] text-slate-400 italic">Add start/due dates to chart</span>
                </div>
              </li>
            );
          }
          const left = pct(start);
          const width = Math.max(2, pct(end) - left);
          const color = STATUS_COLOR[d.status] || "bg-slate-300";
          const progress = Math.max(0, Math.min(100, Number(d.completion_pct) || 0));
          return (
            <li key={d.id} className="flex items-center text-xs hover:bg-slate-50/50"
                data-testid={`gantt-row-${d.id}`}>
              <div className="w-[230px] flex-shrink-0 px-3 py-2 truncate border-r border-slate-100"
                   style={{ paddingLeft: 12 + d.__depth * 16 }}
                   title={d.title}>
                {d.milestone && <Flag size={9} weight="fill" className="inline mr-1 text-amber-600" />}
                <span className={d.__depth > 0 ? "text-slate-700" : "font-medium text-slate-900"}>{d.title || "Untitled"}</span>
              </div>
              <div className="flex-1 relative h-7 group">
                {/* Month gridlines */}
                {months.slice(1).map((m, i) => (
                  <div key={i} className="absolute top-0 bottom-0 border-l border-slate-100" style={{ left: `${pct(m)}%` }} />
                ))}
                {/* Bar */}
                <div
                  className={`absolute top-1.5 h-4 rounded ${color} opacity-60`}
                  style={{ left: `${left}%`, width: `${width}%` }}
                  title={`${formatDate(d.start_date) || "—"} → ${formatDate(d.due_date) || "—"} · ${d.status} · ${progress}%`}
                >
                  {/* Progress fill inside the bar */}
                  <div className={`h-full rounded-l ${color}`} style={{ width: `${progress}%` }} />
                  {/* Inline label */}
                  {width > 14 && (
                    <span className="absolute inset-0 flex items-center justify-end px-1 text-[9px] font-mono-alt text-white font-bold">
                      {progress}%
                    </span>
                  )}
                </div>
                {/* Status badge on hover at right */}
                <span className="absolute right-2 top-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <StatusBadge status={d.status} />
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-3 py-2 text-[10px] font-mono-alt uppercase tracking-wider text-slate-500">
        <span>Status:</span>
        {[
          ["pending", "Pending"], ["in_progress", "In progress"],
          ["review", "Review"], ["approved", "Approved"], ["completed", "Done"],
          ["blocked", "Blocked"],
        ].map(([k, lbl]) => (
          <span key={k} className="inline-flex items-center gap-1">
            <span className={`inline-block h-2 w-3 rounded ${STATUS_COLOR[k]}`} />
            <span>{lbl}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
